import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { tasksApi } from './api/tasks.api';
import type { CreateTaskInput, TaskFilters, TaskStatus } from './types';

export const taskKeys = { list: (f: TaskFilters) => ['tasks', f] as const, detail: (id: string) => ['task', id] as const, approvals: (mine: boolean) => ['tasks', 'approvals', mine] as const, tags: ['tasks', 'tags'] as const };

export function useTasks(filters: TaskFilters, enabled = true) {
  return useQuery({ queryKey: taskKeys.list(filters), queryFn: () => tasksApi.list(filters), enabled, placeholderData: (prev) => prev });
}

export function useTaskDetail(id: string | null) {
  return useQuery({ queryKey: taskKeys.detail(id ?? ''), queryFn: () => tasksApi.detail(id as string), enabled: !!id });
}

function useInvalidateTasks() {
  const client = useQueryClient();
  return (taskId?: string) => {
    void client.invalidateQueries({ queryKey: ['tasks'] });
    void client.invalidateQueries({ queryKey: ['dashboard'] });
    void client.invalidateQueries({ queryKey: ['team-workload'] });
    // project status and progress, the portfolio and the analytics are all derived from the tasks
    for (const key of ['portfolio', 'analytics', 'variance', 'insights', 'projects', 'project']) void client.invalidateQueries({ queryKey: [key] });
    if (taskId) void client.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
  };
}

export function useCreateTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({ mutationFn: (body: CreateTaskInput) => tasksApi.create(body), onSuccess: () => invalidate() });
}

export function useMoveTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({ mutationFn: ({ id, status }: { id: string; status: TaskStatus }) => tasksApi.move(id, status), onSuccess: (t) => invalidate(t.id) });
}

export function useToggleChecklist() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ taskId, itemId, done }: { taskId: string; itemId: string; done: boolean }) => tasksApi.toggleChecklist(taskId, itemId, done),
    onSuccess: (t) => invalidate(t.id)
  });
}

export function useAddChecklistItem() {
  const invalidate = useInvalidateTasks();
  return useMutation({ mutationFn: ({ taskId, label }: { taskId: string; label: string }) => tasksApi.addChecklistItem(taskId, label), onSuccess: (_i, v) => invalidate(v.taskId) });
}

export function useApprovals(mine = false) {
  return useQuery({ queryKey: taskKeys.approvals(mine), queryFn: () => tasksApi.approvals(mine) });
}

export function useApproveTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({ mutationFn: (id: string) => tasksApi.approve(id), onSuccess: (t) => invalidate(t.id) });
}

export function useRejectTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({ mutationFn: ({ id, note }: { id: string; note: string }) => tasksApi.reject(id, note), onSuccess: (t) => invalidate(t.id) });
}

export function useUpdateTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({ mutationFn: ({ id, ...body }: { id: string } & Partial<CreateTaskInput>) => tasksApi.update(id, body), onSuccess: (t) => invalidate(t.id) });
}

export function useTaskTags() {
  return useQuery({ queryKey: taskKeys.tags, queryFn: tasksApi.tags, staleTime: 60_000 });
}

/** A renamed tag is renamed on its tasks too: the boards refresh. */
export function useSaveTag() {
  const client = useQueryClient();
  return useMutation({ mutationFn: tasksApi.saveTag, onSuccess: () => client.invalidateQueries({ queryKey: ['tasks'] }) });
}

export function useDeleteTag() {
  const client = useQueryClient();
  return useMutation({ mutationFn: tasksApi.deleteTag, onSuccess: () => client.invalidateQueries({ queryKey: taskKeys.tags }) });
}
